// Centralized API client for consistent backend communication using Axios
import axios from 'axios';

// Use environment variable or fallback to production URL
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://motionlogic.xyz/api/v1';

// Configure the default axios instance to inherit AuthContext settings
axios.defaults.baseURL = API_BASE_URL;
axios.defaults.timeout = 10000; // 10 second timeout
axios.defaults.headers.common['Content-Type'] = 'application/json';
axios.defaults.withCredentials = true; // Include cookies for auth

// Debug logging for API base URL
console.log('API Base URL configured:', API_BASE_URL);

// Use the default axios instance so it inherits Authorization headers from AuthContext
const axiosInstance = axios;

// Authentication is handled globally via AuthContext
// No need for request interceptor as axios.defaults.headers.common['Authorization'] is set

// Response interceptor for centralized error handling
axiosInstance.interceptors.response.use(
  (response) => {
    // Add debug logging for video uploads with safe property access
    if (response && response.config && response.config.url && response.config.url.includes('/video/upload')) {
      console.log('=== VIDEO UPLOAD RESPONSE DEBUG ===');
      console.log('Response status:', response.status);
      console.log('Response data:', response.data);
      console.log('Response structure:', {
        hasData: response && 'data' in response,
        dataType: response && response.data ? typeof response.data : 'undefined',
        dataKeys: response && response.data && typeof response.data === 'object' ? Object.keys(response.data) : 'no data'
      });
    }
    
    // Handle different response structures more safely
    if (response && response.data !== undefined) {
      return response.data; // Return only the data part
    } else if (response) {
      // Fallback for responses without data property
      return response;
    } else {
      // Edge case: response is null/undefined
      console.warn('Received null/undefined response');
      return null;
    }
  },
  (error) => {
    // Handle different types of errors
    if (error.response) {
      // Special handling for 404s on site-assessment with status filter
      if (error.response.status === 404 && error.config?.url?.includes('/site-assessment')) {
        throw error; // Preserve original axios error structure for specific 404 handling
      }
      
      // Server responded with error status
      const { status, statusText, data } = error.response;
      const errorMessage = data?.message || data?.detail || statusText || 'Request failed';
      throw new Error(`API Error ${status}: ${errorMessage}`);
    } else if (error.request) {
      // Request was made but no response received
      throw new Error('Network error: Unable to connect to server');
    } else {
      // Something else happened
      throw new Error(`Request error: ${error.message}`);
    }
  }
);

const apiClient = {
  // Debug function to check current auth state
  debug: {
    checkAuthHeaders: () => {
      console.log('Current axios defaults:', {
        authorization: axios.defaults.headers.common['Authorization'],
        withCredentials: axios.defaults.withCredentials,
        baseURL: axios.defaults.baseURL
      });
    }
  },

  // Low-level request method (if needed for custom requests)
  async request(endpoint, options = {}) {
    try {
      const config = {
        url: endpoint,
        ...options,
      };
      
      // Handle data vs body for different request methods
      if (options.data && typeof options.data === 'object') {
        config.data = options.data;
      }
      
      const response = await axiosInstance(config);
      return response;
    } catch (error) {
      throw error;
    }
  },

  // Billing endpoints
  billing: {
    getSubscription: () => axiosInstance.get('/billing/subscription'),
    getPlans: () => axiosInstance.get('/billing/plans'),
    getPaymentMethods: () => axiosInstance.get('/billing/payment-methods'),
    getInvoices: () => axiosInstance.get('/billing/invoices'),
    getAccount: () => axiosInstance.get('/billing/account'),
    upgradeSubscription: (planId) => axiosInstance.post('/billing/subscription/upgrade', { 
      plan_id: planId 
    }),
    addPaymentMethod: (paymentMethod) => axiosInstance.post('/billing/payment-methods', 
      paymentMethod
    ),
    downloadInvoice: async (invoiceId) => {
      // Special handling for file downloads
      const response = await axiosInstance.get(`/billing/invoices/${invoiceId}/download`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice-${invoiceId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      return response;
    },
  },

  // Dashboard endpoints - Enhanced with better notification management
  dashboard: {
    getOverview: () => axiosInstance.get('/dashboard/overview'),
    getNotifications: (filters = {}) => axiosInstance.get('/dashboard/notifications', {
      params: filters
    }),
    getTeamMembers: () => axiosInstance.get('/dashboard/team-overview'),
    getUsageStats: (period = 'month') => axiosInstance.get('/dashboard/usage-statistics', {
      params: { period }
    }),
    getAnalyticsSummary: (timeframe = '7d') => axiosInstance.get('/dashboard/analytics-summary', {
      params: { timeframe }
    }),
    
    // Simple notification management
    updateNotification: (notificationId, updates) => 
      axiosInstance.patch(`/dashboard/notifications/${notificationId}`, updates),
    markNotificationRead: (notificationId) => axiosInstance.patch(
      `/dashboard/notifications/${notificationId}`, 
      { read: true }
    ),
    
    // Legacy compatibility
    markNotificationReadLegacy: (notificationId) => {
      console.warn('markNotificationReadLegacy is deprecated. Use markNotificationRead() instead.');
      return axiosInstance.post(`/dashboard/notifications/${notificationId}/mark-read`);
    },
  },

  // Team management endpoints
  team: {
    getMembers: () => axiosInstance.get('/team/members'),
    inviteMember: (inviteData) => axiosInstance.post('/team/invite', inviteData),
    updateMemberRole: (memberId, role) => axiosInstance.put(`/team/members/${memberId}/role`, { role }),
    removeMember: (memberId) => axiosInstance.delete(`/team/members/${memberId}`),
    resendInvite: (memberId) => axiosInstance.post(`/team/members/${memberId}/resend-invite`),
  },

  // Action Plans endpoints - RESTful design for project management
  actionPlans: {
    // Create action plan (replaces confusing "unified-implementation")
    create: (planData) => axiosInstance.post('/action-plans/', planData),
    
    // Get specific action plan
    get: (planId) => axiosInstance.get(`/action-plans/${planId}`),
    
    // List action plans with optional filters
    list: (filters = {}) => axiosInstance.get('/action-plans/', { params: filters }),
    
    // Update action plan
    update: (planId, updates) => axiosInstance.patch(`/action-plans/${planId}`, updates),
    
    // Task management sub-resources
    updateTask: (planId, taskId, updates) => axiosInstance.put(
      `/action-plans/${planId}/tasks/${taskId}`, 
      updates
    ),
    getTasks: (planId) => axiosInstance.get(`/action-plans/${planId}/tasks`),
    
    // Timeline management endpoints
    getTimeline: (planId) => axiosInstance.get(`/action-plans/${planId}/timeline`),
    getTimelinePreview: (params) => axiosInstance.get('/action-plans/timeline/preview', { params }),
    updateTimelineStep: (planId, stepId, updates) => axiosInstance.put(`/action-plans/${planId}/timeline/${stepId}`, updates),
    initializeTimeline: (planId, timelineData) => axiosInstance.post(`/action-plans/${planId}/timeline/initialize`, timelineData),
  },

  // Journey Context endpoints
  journeyContext: {
    save: (contextData) => axiosInstance.post('/journey/context/save', contextData),
    get: (contextId) => axiosInstance.get(`/journey/context/${contextId}`),
    updateStage: (contextId, stage, subStage) => axiosInstance.put(`/journey/context/${contextId}/stage`, { 
      stage, 
      sub_stage: subStage 
    }),
  },

  // Site Assessment endpoints - RESTful design with confidence-based data merging
  siteAssessment: {
    // Create assessment with confidence-based merging
    create: (assessmentData, options = {}) => {
      const params = {};
      if (options.source) params.source = options.source;
      if (options.merge_existing !== undefined) params.merge_existing = options.merge_existing;
      
      return axiosInstance.post('/site-assessment/', assessmentData, { params });
    },
    
    // Get specific assessment
    get: (assessmentId) => axiosInstance.get(`/site-assessment/${assessmentId}/`),
    
    // Update assessment with confidence strategy
    update: (assessmentId, updates, options = {}) => {
      const params = {};
      if (options.source) params.source = options.source;
      if (options.merge_strategy) params.merge_strategy = options.merge_strategy;
      
      return axiosInstance.patch(`/site-assessment/${assessmentId}/`, updates, { params });
    },
    
    // List assessments with optional status filter
    getList: (filters = {}) => axiosInstance.get('/site-assessment/', { params: filters }),
    
    // Legacy compatibility methods (redirect to new RESTful endpoints)
    list: (filters = {}) => axiosInstance.get('/site-assessment/', { params: filters }),
    
    // Submit assessment (update status to PENDING)
    submit: (assessmentId, finalData = {}) => {
      const updates = { ...finalData, status: 'PENDING' };
      return axiosInstance.patch(`/site-assessment/${assessmentId}/`, updates, {
        params: { source: 'user-direct', merge_strategy: 'confidence' }
      });
    },
    
    // Create draft (create with DRAFT status)
    createDraft: (draftData, options = {}) => {
      const assessmentData = { ...draftData, status: 'DRAFT' };
      const defaultOptions = { source: 'form-persistence-/business-case', merge_existing: true };
      return axiosInstance.post('/site-assessment/', assessmentData, { 
        params: { ...defaultOptions, ...options }
      });
    },
    
    // Get drafts (list with status filter)
    getDrafts: () => axiosInstance.get('/site-assessment/', { 
      params: { status: 'DRAFT' } 
    }),
    
    // Check if assessment exists
    exists: (assessmentId) => 
      axiosInstance.get(`/site-assessment/${assessmentId}/exists/`),
    
    // Mark complete (update status to COMPLETED)
    markComplete: (assessmentId, completionData = {}) => {
      const updates = { ...completionData, status: 'COMPLETED' };
      return axiosInstance.patch(`/site-assessment/${assessmentId}/`, updates, {
        params: { source: 'user-direct', merge_strategy: 'confidence' }
      });
    },
    
    // Schedule site visit
    schedule: (assessmentId, scheduleData) => 
      axiosInstance.post(`/site-assessment/${assessmentId}/schedule/`, scheduleData),
    
    // Calculate ROI for assessment
    calculateRoi: (assessmentId, roiParams) => 
      axiosInstance.post(`/site-assessment/${assessmentId}/calculate-roi/`, roiParams),
    
    // Legacy methods for backward compatibility
    getStatus: (options = {}) => {
      // Redirect to list endpoint
      console.warn('getStatus is deprecated, use getList() instead');
      return axiosInstance.get('/site-assessment/', { params: options });
    },
    
    createOrUpdate: (assessmentData, options = {}) => {
      // Redirect to create with merge_existing=true
      console.warn('createOrUpdate is deprecated, use create() with merge_existing option');
      const defaultOptions = { source: 'api-enrichment', merge_existing: true };
      return axiosInstance.post('/site-assessment/', assessmentData, { 
        params: { ...defaultOptions, ...options }
      });
    }
  },

  // Implementation Project endpoints - Enhanced with confidence-based merging
  implementation: {
    // REMOVE confusing "unified-implementation" terminology
    createProject: (projectData, options = {}) => {
      const params = {};
      if (options.source) params.source = options.source;
      if (options.merge_existing !== undefined) params.merge_existing = options.merge_existing;
      return axiosInstance.post('/action-plans/', projectData, { params });
    },
    getProject: (projectId) => axiosInstance.get(`/action-plans/${projectId}`),
    getPhases: (projectId) => axiosInstance.get(`/action-plans/${projectId}/phases`),
    updatePhase: (projectId, phaseId, updates, options = {}) => {
      const params = {};
      if (options.source) params.source = options.source;
      return axiosInstance.patch(`/action-plans/${projectId}/phases/${phaseId}`, updates, { params });
    },
    completeAssessment: (projectId, assessmentData, options = {}) => {
      const params = {};
      if (options.source) params.source = options.source;
      return axiosInstance.post(`/action-plans/${projectId}/complete-assessment`, assessmentData, { params });
    },
  },

  // Business Case endpoints - fully integrated with backend ROI services
  businessCase: {
    create: (caseData) => axiosInstance.post('/business-case/create', caseData),
    generateReport: (caseId) => axiosInstance.get(`/business-case/${caseId}/report`),
    combineWithAssessment: (caseData, assessmentId) => axiosInstance.post('/business-case/with-assessment', { ...caseData, assessment_id: assessmentId }),
  },
  
  // ROI calculation endpoints - centralized service for consistent calculations
  roi: {
    calculateComprehensive: (businessCaseData) => 
      axiosInstance.post('/roi/business-case/comprehensive', businessCaseData),
    
    // Email verification for business case access
    verifyBusinessCase: (token) =>
      axiosInstance.get(`/roi/business-case/verify/${token}`),
    
    // Live preview calculations for interactive user feedback
    calculateLivePreview: (previewData) => 
      axiosInstance.post('/roi/preview', previewData),
    
    // Universal calculator endpoint for component-specific calculations
    calculator: (calculationData) => 
      axiosInstance.post('/roi/calculator', calculationData),
    
    // Get industry-specific defaults for ROI inputs
    getIndustryDefaults: (industryType) => 
      axiosInstance.get(`/roi/industry-defaults${industryType ? `?industry_type=${encodeURIComponent(industryType)}` : ''}`),
    
    // Calculate optimal camera requirements based on site parameters
    calculateCameras: (siteData) => 
      axiosInstance.post('/roi/camera-calculation', siteData),
    
    // Get industry benchmarks for comparison
    getIndustryBenchmarks: (industryType) => 
      axiosInstance.get(`/roi/benchmarks${industryType ? `?industry_type=${encodeURIComponent(industryType)}` : ''}`),
    
    // Quick ROI estimate with minimal inputs
    getQuickEstimate: (params) => 
      axiosInstance.post('/roi/quick-estimate', params),
  },

  // Agent endpoints for LLM analysis
  agent: {
    // Generate comprehensive implementation project summary using all available data
    analyseImplementationProject: (implementationProjectData) =>
      axiosInstance.post('/agent/implementation-summary', implementationProjectData),
    
    // Get recommended next steps and customer insights using LLM
    getCustomerInsights: (customerData) =>
      axiosInstance.post('/agent/recommended-next-steps', customerData),
    
    // Enrich implementation project data with company information from available data sources
    // Fetches company data based on email domain and enriches project context
    // New: Supports preview_only flag for dry-run enrichment preview
    enrichImplementationData: (implementationData, userEmail, previewOnly = false) =>
      axiosInstance.post('/agent/enrich-implementation-data', implementationData, {
        params: { 
          user_email: userEmail,
          preview_only: previewOnly
        }
      }),
    
    // Fetch company data for a given email from available data sources
    // Service-agnostic endpoint that works with any configured company data provider
    getCompanyData: (userEmail) =>
      axiosInstance.get(`/agent/company-data/${encodeURIComponent(userEmail)}`),
    
    // Sync user registration to external CRM systems (triggers company creation and enrichment)
    // New: Supports background enrichment scheduling
    syncUserRegistration: (userEmail, registrationData = null, enableBackgroundEnrichment = true) =>
      axiosInstance.post('/agent/sync-user-registration', {
        user_email: userEmail,
        registration_data: registrationData,
        enable_background_enrichment: enableBackgroundEnrichment
      }),
    
    // Check the status of a background company enrichment task
    // New: Monitor background enrichment progress and results
    getEnrichmentTaskStatus: (taskId) =>
      axiosInstance.get(`/agent/enrichment-task-status/${encodeURIComponent(taskId)}`),
    
    // Preview what implementation form data can be pre-filled from available company data
    // Service-agnostic endpoint showing available enrichment data
    previewImplementationPrefill: (userEmail) =>
      axiosInstance.get(`/agent/implementation-prefill-preview/${encodeURIComponent(userEmail)}`),
    
    // Health check for agent service
    healthCheck: () =>
      axiosInstance.get('/agent/health'),
      
    // Legacy endpoints for backwards compatibility (deprecated)
    // These will be removed in a future version
    getHubSpotCompanyData: (userEmail) => {
      console.warn('getHubSpotCompanyData is deprecated. Use getCompanyData instead.');
      return axiosInstance.get(`/agent/company-data/${encodeURIComponent(userEmail)}`);
    },
  },

  // Video endpoints
  video: {
    // Upload video file directly to backend
    upload: async (file, serviceId = 'demo', onProgress = null) => {
      // Check file size limit (adjust based on your server's limit)
      const maxSizeBytes = 500 * 1024 * 1024; // 500 MB limit (matches server)
      if (file.size > maxSizeBytes) {
        throw new Error(`File size (${Math.round(file.size / 1024 / 1024)} MB) exceeds maximum allowed size (${Math.round(maxSizeBytes / 1024 / 1024)} MB). Please compress your video or use a smaller file.`);
      }

      const formData = new FormData();
      formData.append('file', file);
      formData.append('service_id', serviceId);

      // Enhanced debugging for upload issues
      console.log('=== VIDEO UPLOAD DEBUG ===');
      console.log('Uploading video to:', `${API_BASE_URL}/video/upload`);
      console.log('File:', { 
        name: file.name, 
        size: file.size, 
        sizeInMB: Math.round(file.size / 1024 / 1024), 
        type: file.type 
      });
      console.log('Service ID:', serviceId);
      console.log('Current axios config:', {
        baseURL: axiosInstance.defaults.baseURL,
        withCredentials: axiosInstance.defaults.withCredentials,
        timeout: axiosInstance.defaults.timeout,
        authorization: axiosInstance.defaults.headers.common['Authorization']
      });

      try {
        const response = await axiosInstance.post('/video/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          timeout: 300000, // 5 minute timeout for large file uploads
          // Override global defaults for this specific request
          withCredentials: true,
          // Add progress tracking
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            console.log(`Upload progress: ${percentCompleted}%`);
            
            // Call the progress callback if provided
            if (onProgress && typeof onProgress === 'function') {
              onProgress(percentCompleted);
            }
          },
        });

        console.log('Upload successful:', response);
        
        // Debug the response structure
        console.log('Response type:', typeof response);
        console.log('Response keys:', Object.keys(response || {}));
        
        return response;
      } catch (error) {
        console.error('=== UPLOAD ERROR DETAILS ===');
        console.error('Error object:', error);
        console.error('Error response:', error.response);
        console.error('Error request:', error.request);
        console.error('Error config:', error.config);
        
        // Provide more specific error messages
        if (error.response?.status === 413) {
          throw new Error(`File too large. Server rejected the ${Math.round(file.size / 1024 / 1024)} MB file. Try compressing your video or contact support to increase the upload limit.`);
        } else if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
          throw new Error(`Upload timeout. The ${Math.round(file.size / 1024 / 1024)} MB file took too long to upload. Try with a smaller file or check your internet connection.`);
        }
        
        // Re-throw the error to be handled by the response interceptor
        throw error;
      }
    },
    
    // Start video processing
    startProcessing: (uploadId, serviceId, filename, userEmail = null) =>
      axiosInstance.post('/video/process', {
        upload_id: uploadId,
        service_id: serviceId,
        filename,
        user_email: userEmail
      }),
    
    // Update upload with email for progressive auth
    // Uses upload_id directly for simplicity
    updateUploadEmail: (email, uploadId) =>
      axiosInstance.post('/video/update-email', {
        email: email,
        upload_id: uploadId
      }),
    
    // Get processing status
    getStatus: (uploadId) => axiosInstance.get(`/video/status/${uploadId}`),
    
    // Get analysis results
    getResults: (uploadId) => axiosInstance.get(`/video/results/${uploadId}`),
  },

  // Task tracking for journey progression
  tasks: {
    // Get completion status for a specific task type
    getTaskStatus: (taskType) => axiosInstance.get(`/tasks/${taskType}/status`),
    
    // Get all completed task types for current user
    getAllCompletedTasks: () => axiosInstance.get('/tasks/completed'),
    
    // Mark a task type as completed (creates new task instance)
    markTaskCompleted: (taskType, metadata = {}) => 
      axiosInstance.post(`/tasks/${taskType}/complete`, metadata),
    
    // Get task requirements and dependencies for a task type
    getTaskRequirements: (taskType) => 
      axiosInstance.get(`/tasks/${taskType}/requirements`),
  },

  // User Projects Management - Site-centric data architecture
  userProjects: {
    // Get all sites for the authenticated user
    getAllSites: () => axiosInstance.get('/user/sites'),
    
    // Get specific site with all associated data
    getSite: (siteId) => axiosInstance.get(`/user/sites/${siteId}`),
    
    // Create a new site
    createSite: (siteData) => axiosInstance.post('/user/sites', siteData),
    
    // Update site information
    updateSite: (siteId, updates) => axiosInstance.put(`/user/sites/${siteId}`, updates),
    
    // Delete a site
    deleteSite: (siteId) => axiosInstance.delete(`/user/sites/${siteId}`),
    
    // Get all analyses for a specific site
    getSiteAnalyses: (siteId) => axiosInstance.get(`/user/sites/${siteId}/analyses`),
    
    // Add analysis to a site
    associateAnalysis: (siteId, analysisData) => 
      axiosInstance.post(`/user/sites/${siteId}/analyses`, analysisData),
    
    // Get analysis details
    getAnalysis: (siteId, analysisId) => 
      axiosInstance.get(`/user/sites/${siteId}/analyses/${analysisId}`),
    
    // Update analysis
    updateAnalysis: (siteId, analysisId, updates) => 
      axiosInstance.put(`/user/sites/${siteId}/analyses/${analysisId}`, updates),
    
    // Delete analysis
    deleteAnalysis: (siteId, analysisId) => 
      axiosInstance.delete(`/user/sites/${siteId}/analyses/${analysisId}`),
    
    // Get business case for a site
    getSiteBusinessCase: (siteId) => 
      axiosInstance.get(`/user/sites/${siteId}/business-case`),
    
    // Update business case for a site
    updateSiteBusinessCase: (siteId, businessCaseData) => 
      axiosInstance.put(`/user/sites/${siteId}/business-case`, businessCaseData),
    
    // Get implementation plan for a site
    getSiteImplementation: (siteId) => 
      axiosInstance.get(`/user/sites/${siteId}/implementation`),
    
    // Update implementation plan for a site
    updateSiteImplementation: (siteId, implementationData) => 
      axiosInstance.put(`/user/sites/${siteId}/implementation`, implementationData),
    
    // Migrate localStorage data to user account
    migrateLocalData: (localData) => 
      axiosInstance.post('/user/migrate-local-data', localData),
    
    // Get current active site
    getActiveSite: () => axiosInstance.get('/user/active-site'),
    
    // Set active site
    setActiveSite: (siteId) => axiosInstance.put('/user/active-site', { siteId }),
    
    // Export site data
    exportSite: (siteId, format = 'json') => 
      axiosInstance.get(`/user/sites/${siteId}/export?format=${format}`),
    
    // Get user's project statistics
    getProjectStats: () => axiosInstance.get('/user/project-stats'),
    
    // Simple project management operations
    markComplete: (siteId, completionData = {}) => {
      const updates = { ...completionData, status: 'COMPLETED' };
      return axiosInstance.patch(`/user/sites/${siteId}`, updates);
    },
    
    // Legacy compatibility
    getStatus: (options) => {
      console.warn('getStatus is deprecated, use list() with filters instead');
      return axiosInstance.get('/user/sites', { params: options });
    },
    createOrUpdate: (data, options = {}) => {
      console.warn('createOrUpdate is deprecated, use createSite() instead');
      return this.createSite(data);
    },
  },

  // CPQ (Configure, Price, Quote) API - Enhanced with quote management and currency conversion
  cpq: {
    calculatePricing: (configuration) =>
      axiosInstance.post('/cpq/calculate', configuration),
    
    // Get available configuration options
    getConfigurationOptions: () =>
      axiosInstance.get('/cpq/configuration-options'),
    
    // Validate a configuration
    validateConfiguration: (configuration) =>
      axiosInstance.post('/cpq/validate', configuration),
    
    // Currency conversion endpoints
    convertCurrency: (amount, fromCurrency, toCurrency) =>
      axiosInstance.post('/cpq/currency/convert', {
        amount,
        from_currency: fromCurrency,
        to_currency: toCurrency
      }),
    
    // Get exchange rate between currencies
    getExchangeRate: (fromCurrency, toCurrency) =>
      axiosInstance.get(`/cpq/currency/rate?from_currency=${fromCurrency}&to_currency=${toCurrency}`),
    
    // Get all supported currencies
    getSupportedCurrencies: () =>
      axiosInstance.get('/cpq/currency/supported'),
  },

  // Form Data persistence endpoints - using site assessment with draft status
  formData: {
    save: (formData) => {
      // Transform generic form data to site assessment draft format
      const draftData = {
        ...formData.data,
        route: formData.route,
        metadata: formData.metadata
      };
      
      // Convert array fields to strings as expected by the backend
      if (Array.isArray(draftData.primary_safety_concerns)) {
        draftData.primary_safety_concerns = draftData.primary_safety_concerns.join(', ') || '';
      }
      if (Array.isArray(draftData.high_risk_areas)) {
        draftData.high_risk_areas = draftData.high_risk_areas.join(', ') || '';
      }
      if (Array.isArray(draftData.manufacturing_subtypes)) {
        draftData.manufacturing_subtypes = draftData.manufacturing_subtypes.join(', ') || '';
      }
      if (Array.isArray(draftData.improvement_methods)) {
        draftData.improvement_methods = draftData.improvement_methods.join(', ') || '';
      }
      
      return axiosInstance.post('/site-assessment/', draftData, {
        params: { source: 'form-persistence-/business-case', merge_existing: true }
      });
    },
    get: (formType, route) => {
      if (formType === 'site-assessment' || formType === 'business-case') {
        return axiosInstance.get('/site-assessment/', { 
          params: { status: 'DRAFT' } 
        }).then(drafts => {
          // Find draft matching the route
          const matchingDraft = drafts.find(draft => draft.route === route);
          if (matchingDraft) {
            return {
              data: matchingDraft,
              updated_at: matchingDraft.updated_at
            };
          }
          throw new Error('Form data not found');
        });
      }
      throw new Error('Unsupported form type');
    },
    delete: (formType, route) => {
      if (formType === 'site-assessment' || formType === 'business-case') {
        return axiosInstance.get('/site-assessment/', { 
          params: { status: 'DRAFT' } 
        }).then(drafts => {
          const matchingDraft = drafts.find(draft => draft.route === route);
          if (matchingDraft) {
            return axiosInstance.delete(`/site-assessment/${matchingDraft.id}`);
          }
          return { status: true, message: 'No draft to delete' };
        });
      }
      throw new Error('Unsupported form type');
    },
    list: (formType) => {
      if (formType === 'site-assessment' || formType === 'business-case') {
        return axiosInstance.get('/site-assessment/', { 
          params: { status: 'DRAFT' } 
        }).catch(error => {
          // If 404, it means no drafts exist - return empty array
          if (error.response?.status === 404) {
            return [];
          }
          // Re-throw other errors
          throw error;
        });
      }
      throw new Error('Unsupported form type');
    },
    sync: (localFormData) => {
      // Convert local form data to draft format and save each one
      const syncPromises = localFormData.map(formData => {
        const draftData = {
          ...formData.data,
          route: formData.route,
          metadata: { ...formData.metadata, synced_from_anonymous: true }
        };
        
        // Convert array fields to strings as expected by the backend
        if (Array.isArray(draftData.primary_safety_concerns)) {
          draftData.primary_safety_concerns = draftData.primary_safety_concerns.join(', ') || '';
        }
        if (Array.isArray(draftData.high_risk_areas)) {
          draftData.high_risk_areas = draftData.high_risk_areas.join(', ') || '';
        }
        if (Array.isArray(draftData.manufacturing_subtypes)) {
          draftData.manufacturing_subtypes = draftData.manufacturing_subtypes.join(', ') || '';
        }
        if (Array.isArray(draftData.improvement_methods)) {
          draftData.improvement_methods = draftData.improvement_methods.join(', ') || '';
        }
        
        return axiosInstance.post('/site-assessment/draft', draftData);
      });
      return Promise.all(syncPromises).then(results => ({
        status: true,
        message: `Successfully synced ${results.length} form drafts to your account`
      }));
    },
  },

  journey: {
    save: (contextData) => axiosInstance.post('/journey/context/save', contextData),
    get: (contextId) => axiosInstance.get(`/journey/context/${contextId}`),
    updateStage: (contextId, stage, subStage) => axiosInstance.put(`/journey/context/${contextId}/stage`, { 
      stage, 
      sub_stage: subStage 
    }),
    markComplete: (contextId, completionData = {}) => {
      const updates = { ...completionData, status: 'COMPLETED' };
      return axiosInstance.patch(`/journey/context/${contextId}`, updates);
    },
    
    // Legacy compatibility
    getStatus: (options) => {
      console.warn('getStatus is deprecated, use get() instead');
      return axiosInstance.get('/journey/context', { params: options });
    },
    createOrUpdate: (data, options = {}) => {
      console.warn('createOrUpdate is deprecated, use save() instead');
      return this.save(data);
    },
  },

  // User Profile endpoints - confidence merging only for enrichment
  userProfile: {
    // Standard CRUD operations
    get: () => axiosInstance.get('/user/profile'),
    update: (data) => axiosInstance.put('/user/profile', data),
    
    // Enrichment endpoint with confidence merging (for company data, etc.)
    enrichProfile: (enrichmentData, options = {}) => {
      const params = {};
      if (options.source) params.source = options.source;
      if (options.merge_existing !== undefined) params.merge_existing = options.merge_existing;
      return axiosInstance.post('/user/profile/enrich', enrichmentData, { params });
    },
    
    // Legacy compatibility
    getStatus: (options) => {
      console.warn('getStatus is deprecated, use get() instead');
      return axiosInstance.get('/user/profile', { params: options });
    },
    createOrUpdate: (data, options = {}) => {
      console.warn('createOrUpdate is deprecated, use update() for profile changes or enrichProfile() for enrichment');
      return this.update(data);
    },
  },
};

// Export both the configured axios instance and the apiClient object
export { axiosInstance };
export default apiClient;
